import { create } from "zustand"

type TransitionPhase = "idle" | "exiting" | "entering"
type ProjectActivationPhase = "idle" | "activating"
type Vec3Tuple = [number, number, number]

interface CameraTarget {
  position: Vec3Tuple
  lookAt: Vec3Tuple
}

interface ThumbnailSize {
  width: number
  height: number
}

export const CAMERA_POSITIONS = {
  home: { position: [0, 0, -4] as [number, number, number], lookAt: [0, 0, -15] as [number, number, number] },
  projects: { position: [0, 0, -4] as [number, number, number], lookAt: [0, 0, -15] as [number, number, number] },
  project: { position: [0, 0, -4] as [number, number, number], lookAt: [0, 0, -15] as [number, number, number] },
  contact: { position: [0, 0, -4] as [number, number, number], lookAt: [0, 0, -15] as [number, number, number] },
  tags: { position: [0, 0, -4] as [number, number, number], lookAt: [0, 0, -15] as [number, number, number] },
  overview: { position: [0, 0, 8] as [number, number, number], lookAt: [0, 0, 0] as [number, number, number] },
} as const

export type RouteKey = keyof typeof CAMERA_POSITIONS

export function getGallerySetSlugFromPath(pathname: string): string {
  if (pathname === "/projects") return "default"
  if (pathname.startsWith("/projects/")) {
    const slug = pathname.slice("/projects/".length).split("/")[0]
    return slug || "default"
  }
  return "default"
}

export function resolveRouteKey(pathname: string): RouteKey {
  if (pathname === "/" || pathname === "/home") return "home"
  if (pathname === "/overview") return "overview"
  if (pathname === "/projects" || pathname.startsWith("/projects/")) return "projects"
  if (pathname.startsWith("/tags/")) return "tags"
  if (pathname.startsWith("/project/")) return "project"
  if (pathname === "/contact") return "contact"
  return "home"
}

interface TransitionState {
  phase: TransitionPhase
  targetRoute: string | null
  pendingProjectRoute: string | null
  projectActivationPhase: ProjectActivationPhase
  shouldCommitPendingProjectRoute: boolean
  cameraTarget: CameraTarget | null
  selectedThumbnailPosition: Vec3Tuple | null
  selectedThumbnailSize: ThumbnailSize | null
  isAnimating: boolean
  currentPath: string

  // Actions
  initiateTransition: (route: string, cameraTarget: CameraTarget) => void
  setPendingProjectRoute: (route: string | null) => void
  setProjectActivationPhase: (phase: ProjectActivationPhase) => void
  requestPendingProjectRouteCommit: () => void
  clearPendingProjectRouteCommit: () => void
  setPhase: (phase: TransitionPhase) => void
  setSelectedThumbnailPosition: (pos: Vec3Tuple | null) => void
  setSelectedThumbnailSize: (size: ThumbnailSize | null) => void
  setIsAnimating: (animating: boolean) => void
  setCurrentPath: (path: string) => void
  reset: () => void
}

export const useTransitionStore = create<TransitionState>((set) => ({
  phase: "idle",
  targetRoute: null,
  pendingProjectRoute: null,
  projectActivationPhase: "idle",
  shouldCommitPendingProjectRoute: false,
  cameraTarget: null,
  selectedThumbnailPosition: null,
  selectedThumbnailSize: null,
  isAnimating: false,
  currentPath: typeof window !== "undefined" ? window.location.pathname : "/",

  initiateTransition: (route, cameraTarget) =>
    set({
      phase: "exiting",
      targetRoute: route,
      cameraTarget,
    }),

  setPendingProjectRoute: (route) => set({ pendingProjectRoute: route, shouldCommitPendingProjectRoute: false }),
  setProjectActivationPhase: (projectActivationPhase) => set({ projectActivationPhase }),
  requestPendingProjectRouteCommit: () => set({ shouldCommitPendingProjectRoute: true }),
  clearPendingProjectRouteCommit: () => set({ shouldCommitPendingProjectRoute: false }),

  setPhase: (phase) => set({ phase }),

  setSelectedThumbnailPosition: (pos) => set({ selectedThumbnailPosition: pos }),
  setSelectedThumbnailSize: (size) => set({ selectedThumbnailSize: size }),

  setIsAnimating: (animating) => set({ isAnimating: animating }),

  setCurrentPath: (path) => set({ currentPath: path }),

  reset: () =>
    set({
      phase: "idle",
      targetRoute: null,
      pendingProjectRoute: null,
      projectActivationPhase: "idle",
      shouldCommitPendingProjectRoute: false,
      cameraTarget: null,
      selectedThumbnailPosition: null,
      selectedThumbnailSize: null,
    }),
}))
